import angular from 'angular';
import epfl_authgeoportailSearchComponent from '../search/component.js';
import ngeoMapFeatureOverlayMgr from 'ngeo/map/FeatureOverlayMgr';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olStyleCircle from 'ol/style/Circle.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleIcon from 'ol/style/Icon.js';
import * as olBase from 'ol/index.js';

/**
 * @private
 */
class Controller {
  /**
   * @param {angular.IHttpService} $http Angular $http service.
   * @param {angular.IScope} $scope Angular scope.
   * @param {import("ngeo/statemanager/Location.js").StatemanagerLocation} ngeoLocation ngeo location service.
   * @param {string} routingUrl The URL to the routing service.
   * @param {string} fulltextsearchUrl The URL to the fulltext search service.
   * @param {string} assetsBaseUrl The base URL to the assets (static-ngeo).
   */
  constructor($http, $scope, ngeoLocation, routingUrl, fulltextsearchUrl, assetsBaseUrl) {
    // Injected properties

    /**
     * @type {angular.$http}
     * @private
     */
    this.$http_ = $http;

    /**
     * @type {angular.Scope}
     * @private
     */
    this.$scope_ = $scope;

    /**
     * @type {string}
     * @private
     */
    this.routingUrl_ = routingUrl;

    /**
     * @type {string}
     * @private
     */
    this.fulltextsearchUrl_ = fulltextsearchUrl;

    /**
     * @type {ngeo.statemanager.Location}
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * @type {string|undefined}
     */
    this.routingFrom = this.ngeoLocation_.getParam('from');

    /**
     * @type {string|undefined}
     */
    this.routingTo = this.ngeoLocation_.getParam('to');

    /**
     * @type {ol.Map}
     * @export
     */
    this.map;

    /**
     * @type {ol.style.Style}
     * @private
     */
    this.lineStyle_ = new olStyleStyle({
      stroke: new olStyleStroke({
        color: [51, 153, 204],
        width: 3,
      }),
    });

    const whiteCircle = new olStyleStyle({
      image: new olStyleCircle({
        radius: 12,
        fill: new olStyleFill({
          color: [255, 255, 255],
        }),
        stroke: new olStyleStroke({
          width: 2,
          color: [211, 211, 211],
        }),
      }),
    });

    /**
     * @type {Array.<ol.style.Style>}
     * @private
     */
    this.fromStyle_ = [
      whiteCircle,
      new olStyleStyle({
        image: new olStyleIcon({
          src: `${assetsBaseUrl}images/routing/depart_red.svg`,
        }),
      }),
    ];

    /**
     * @type {Array.<ol.style.Style>}
     * @private
     */
    this.toStyle_ = [
      whiteCircle,
      new olStyleStyle({
        image: new olStyleIcon({
          src: `${assetsBaseUrl}images/routing/destination_red.svg`,
        }),
      }),
    ];

    /**
     * @type {Array.<ol.style.Style>}
     * @private
     */
    this.notDefinedStyle_ = [new olStyleStyle({})];

    /**
     * @type {import('ngeo/map/FeatureOverlay').FeatureOverlay}
     * @private
     */
    this.featureOverlay_ = ngeoMapFeatureOverlayMgr.getFeatureOverlay();

    this.featureOverlay_.setStyle((feature, resolution) => {
      const type = feature.get('type');
      if (!type) {
        return this.lineStyle_;
      } else if (type === 'start') {
        return this.fromStyle_;
      } else if (type === 'end') {
        return this.toStyle_;
      } else {
        return this.notDefinedStyle_;
      }
    });
  }

  $onInit() {
    const from = this.ngeoLocation_.getParam('from');
    const to = this.ngeoLocation_.getParam('to');

    if (from && to) {
      const promiseFrom = this.$http_.get(`${this.fulltextsearchUrl_}&query=${from}*`);
      const promiseTo = this.$http_.get(`${this.fulltextsearchUrl_}&query=${to}*`);
      Promise.all([promiseFrom, promiseTo]).then((values) => {
        const config = {
          params: {
            from: values[0]['data']['features'][0]['properties']['vertex_id'],
            to: values[1]['data']['features'][0]['properties']['vertex_id'],
          },
        };
        const request = this.$http_.get(this.routingUrl_, config);
        request.then(
          (response) => this.routingSuccess_(response),
          (error) => this.routingError_(error),
        );
      });
    }
  }

  routingSuccess_(response) {
    const parser = new olFormatGeoJSON();
    const readOptions = {
      dataProjection: 'EPSG:21781',
      featureProjection: 'EPSG:21781',
    };
    const line = parser.readFeature(response.data.feature, readOptions);

    // openlayers doesn't like id === null
    line.setId(line.getId() === null ? olBase.getUid(line) : line.getId());
    this.featureOverlay_.addFeature(line);

    const geometry = line.getGeometry();
    const size = this.map.getSize();
    this.map.getView().fit(geometry, size);

    const steps = [];
    for (let i = 0; i < response.data['roadmap'].length; i++) {
      const step = response.data['roadmap'][i];
      const feature = parser.readFeature(step, readOptions);
      steps.push(feature);
      this.featureOverlay_.addFeature(feature);
    }
  }
}
Controller.$inject = ['$http', '$scope', 'ngeoLocation', 'routingUrl', 'fulltextsearchUrl', 'assetsBaseUrl'];

/**
 * @hidden
 */
const gmfModule = angular.module('epflIframeRouting', [epfl_authgeoportailSearchComponent.name]);

gmfModule.component('epflIframeRouting', {
  bindings: {
    map: '=',
  },
  controller: Controller,
});

export default gmfModule;
